import {
  QueryOperator,
  MATCH_QUERY_OPERATORS,
  MatchQueryOperators,
} from "types";
import { useEffect, useMemo, useRef, useState } from "react";
import QueryModal from "views/components/QueryModal";
import TagIcon from "assets/icons/tagIcon";
import QueryModalInput from "views/components/QueryModal/QueryModalInput";
import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import SubmitQueryButton from "views/components/QueryModal/SubmitQueryButton";
import QueryModalOperators from "views/components/QueryModal/QueryModalOperators";

import styles from "./style.module.scss";
import useVqlParams from "hooks/useVqlParams";
import { useDispatch } from "react-redux";
import { setSelectedQueryModal } from "redux/SingleDataset/actions";
import {
  createAndUpdateLabelsFilter,
  isVqlQuery,
  getQueryValuesForFilter,
} from "helpers/utility/vqlHelpers";
import {
  convertAPIOperator,
  handleQuerySelection,
} from "helpers/utility/queryFilterHelpers";
import { VqlQuery } from "types";
import { useMultiSelectQueryModal } from "hooks/UseQueryModal/useMultiSelectQueryModal";
import { useSearchParams } from "react-router-dom";
import { METADATA_SOURCES_PARAMETER } from "../../../../../../helpers/constants/miscellaneous";
import { useLabelsData, LabelItem } from "./useLabelsData";
interface LabelsQueryModalProps {
  editQueryId?: string;
}

export default function LabelsQueryModal({
  editQueryId,
}: LabelsQueryModalProps) {
  const [searchParams] = useSearchParams();
  const metadataSources = searchParams.get(METADATA_SOURCES_PARAMETER);

  const { labels: labelsData, isLoading } = useLabelsData();

  const {
    vqlQueries,
    addVqlQuery,
    findVqlQuery,
    updateVqlQuery,
    removeVqlQuery,
  } = useVqlParams();

  const oldQuery = editQueryId ? findVqlQuery(editQueryId, "labels") : null;
  const queryOperator = oldQuery
    ? convertAPIOperator(oldQuery?.labels?.op)
    : QueryOperator.IS_ONE_OF;
  const [selectedOperator, setSelectedOperator] = useState(queryOperator);
  const existingQuery = useMemo(() => {
    return (
      vqlQueries.find(
        (query: VqlQuery) =>
          isVqlQuery(query, "labels") &&
          convertAPIOperator(query.labels?.op) === selectedOperator,
      ) || null
    );
  }, [vqlQueries, selectedOperator]);

  const queryValues = useMemo(() => {
    return getQueryValuesForFilter(
      editQueryId,
      oldQuery,
      selectedOperator,
      queryOperator,
      existingQuery,
      "labels",
    );
  }, [editQueryId, oldQuery, selectedOperator, queryOperator, existingQuery]);

  const datasetLabelsData = labelsData.map((label: LabelItem) => ({
    ...label,
    selected: queryValues.includes(label.text),
  }));

  const selectedDatasetLabelIds = datasetLabelsData
    .filter((label: LabelItem) => label.selected)
    .map((label: LabelItem) => label.id);

  const [searchText, setSearchText] = useState("");
  const [labels, setLabels] = useState(datasetLabelsData);
  const dispatch = useDispatch();

  useEffect(() => {
    let labelsData = datasetLabelsData;
    if (metadataSources?.length) {
      labelsData = labelsData.filter(({ source }: any) => {
        if (metadataSources.includes("vl_flywheel_v00")) {
          return source === "VL";
        }
        if (metadataSources.includes("user_labels_annotations_file")) {
          return source === "USER";
        }
        return false;
      });
    }
    setLabels(labelsData);
    // eslint-disable-next-line
  }, [selectedOperator, metadataSources, isLoading]);

  const selectedLabelIds = labels
    .filter((label: LabelItem) => label.selected)
    .map((label: LabelItem) => label.id);

  const modalRef = useRef<HTMLDivElement>(null);

  const { isSubmitDisabled } = useMultiSelectQueryModal({
    selectedIds: selectedLabelIds,
    previousIds: selectedDatasetLabelIds,
    selectedOperator,
    initialOperator: queryOperator,
    editQueryId,
    existingQueryId: existingQuery?.id,
  });
  const handleRemoveLabel = (id: number | string) => {
    const newLabels = labels.map((label: LabelItem) => {
      if (label.id === id) {
        return { ...label, selected: false };
      }

      return label;
    });
    setLabels(newLabels);
  };

  const handleSelectLabel = (id: number | string) => {
    const newLabels = labels.map((label: LabelItem) => {
      if (label.id === id) {
        return { ...label, selected: !label.selected };
      }
      return label;
    });

    setLabels(newLabels);
  };

  const handleOptionSelection = (operator: QueryOperator) => {
    setSelectedOperator(operator);
  };

  const handleInputChange = (value: string) => {
    setSearchText(value);
  };

  const handleSubmission = () => {
    if (isSubmitDisabled) {
      return;
    }

    // If no selections, remove the query
    if (selectedLabelIds.length === 0) {
      handleQuerySelection({
        editQueryId,
        existingQueryId: existingQuery?.id,
        removeVqlQuery,
        dispatch,
      });
      return;
    }

    const vqlQuery = createAndUpdateLabelsFilter(
      labels
        .filter((label: LabelItem) => selectedLabelIds.includes(label.id))
        .map((label: LabelItem) => label.text),
      selectedOperator as MatchQueryOperators,
      editQueryId || existingQuery?.id,
    );

    editQueryId || existingQuery
      ? updateVqlQuery(vqlQuery)
      : addVqlQuery(vqlQuery);
    dispatch(setSelectedQueryModal(null));
  };

  return (
    <QueryModal modalRef={modalRef}>
      <QueryModalTitle icon={<TagIcon />}>Labels</QueryModalTitle>
      <div className={styles.filterContent}>
        <QueryModalOperators
          operators={[...MATCH_QUERY_OPERATORS]}
          selectedOperator={selectedOperator}
          onSelect={handleOptionSelection}
        />
        <QueryModalInput
          mode="multiple"
          options={labels}
          searchText={searchText}
          selected={selectedLabelIds}
          onRemove={handleRemoveLabel}
          onSelect={handleSelectLabel}
          onInputChange={handleInputChange}
          isLoading={isLoading}
        />
        <SubmitQueryButton
          isDisabled={isSubmitDisabled}
          onSubmit={handleSubmission}
        />
      </div>
    </QueryModal>
  );
}
